import { buildStandardPulseScaleOptions } from '@/views/pulse/core/buildStandardPulseScaleOptions';

export const SURVEY_DRAFT_STORAGE_VERSION = 1;
export const SURVEY_VIEW_MODE_STORAGE_KEY = 'kincode-pulse-survey-view-mode';
export const SURVEY_VIEW_MODE_STEPPER = 'stepper';
export const SURVEY_VIEW_MODE_LIST = 'list';

/** Debounce before stepper advances after a closed (scale/multiselect) answer. */
export const SURVEY_STEPPER_AUTO_ADVANCE_MS = 400;

/**
 * @param {string} campaignID
 * @param {string} companyID
 * @param {string} employeeID
 * @returns {string}
 */
export function buildSurveyDraftStorageKey(campaignID, companyID, employeeID) {
  return `kincode-pulse-survey:${campaignID}:${companyID}:${employeeID}`;
}

/**
 * @param {string | null | undefined} url
 * @returns {boolean}
 */
export function isSafeRemoteImageUrl(url) {
  const trimmed = url?.trim() ?? '';
  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

/**
 * @param {{ question?: { _id?: { toString?: () => string } } } | null | undefined} questionEntry
 * @param {number} index
 * @returns {string}
 */
export function getQuestionKey(questionEntry, index) {
  const questionId = questionEntry?.question?._id?.toString?.();
  return questionId || `question-${index}`;
}

/**
 * @param {{ question?: { type?: string }, isOptional?: boolean } | null | undefined} questionEntry
 * @returns {boolean}
 */
function isOptionalOpenSurveyQuestion(questionEntry) {
  return (
    questionEntry?.question?.type === 'open' && questionEntry?.isOptional === true
  );
}

/**
 * @param {{ question?: { type?: string }, isOptional?: boolean } | null | undefined} questionEntry
 * @param {unknown} value
 * @returns {boolean}
 */
export function isQuestionAnswered(questionEntry, value) {
  const questionType = questionEntry?.question?.type;

  if (questionType === 'multiselect') {
    return Array.isArray(value) && value.length > 0;
  }

  if (
    isOptionalOpenSurveyQuestion(questionEntry) &&
    (value == null || (typeof value === 'string' && value.trim() === ''))
  ) {
    return true;
  }

  return typeof value === 'string' && value.trim() !== '';
}

/**
 * True when the taker provided a concrete answer (blank optional opens count as false).
 * Use for progress UI; use {@link isQuestionAnswered} for submit readiness.
 * @param {{ question?: { type?: string } } | null | undefined} questionEntry
 * @param {unknown} value
 * @returns {boolean}
 */
export function hasSurveyAnswerValue(questionEntry, value) {
  const questionType = questionEntry?.question?.type;

  if (questionType === 'multiselect') {
    return Array.isArray(value) && value.length > 0;
  }

  return typeof value === 'string' && value.trim() !== '';
}

/**
 * @param {{ question?: { type?: string } } | null | undefined} questionEntry
 * @param {unknown} value
 * @returns {boolean}
 */
function hasAnswerValue(questionEntry, value) {
  return hasSurveyAnswerValue(questionEntry, value);
}

/**
 * @param {unknown} rawAnswers
 * @param {Array<{ question?: { type?: string, _id?: { toString?: () => string } } }>} questions
 * @returns {Record<string, string | string[]>}
 */
export function normalizeDraftAnswers(rawAnswers, questions) {
  if (!rawAnswers || typeof rawAnswers !== 'object') {
    return {};
  }

  return questions.reduce((accumulator, questionEntry, index) => {
    const questionKey = getQuestionKey(questionEntry, index);
    const draftValue = rawAnswers[questionKey];

    if (questionEntry?.question?.type === 'multiselect') {
      if (Array.isArray(draftValue)) {
        accumulator[questionKey] = draftValue.filter(
          (value) => typeof value === 'string' && value.trim() !== ''
        );
      }
      return accumulator;
    }

    if (typeof draftValue === 'string') {
      accumulator[questionKey] = draftValue;
    }

    return accumulator;
  }, {});
}

/**
 * @param {string | null} storageKey
 * @returns {{ version: number, answersByQuestionId?: Record<string, unknown>, currentStep?: number } | null}
 */
export function readSurveyDraft(storageKey) {
  if (typeof window === 'undefined' || !storageKey) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (parsed?.version !== SURVEY_DRAFT_STORAGE_VERSION) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param {string | null} storageKey
 * @param {unknown} value
 */
export function saveSurveyDraft(storageKey, value) {
  if (typeof window === 'undefined' || !storageKey) {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore private mode / quota failures. Survey answering must still work.
  }
}

/**
 * @param {string | null} storageKey
 */
export function clearSurveyDraft(storageKey) {
  if (typeof window === 'undefined' || !storageKey) {
    return;
  }

  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Ignore storage failures on cleanup.
  }
}

/**
 * @param {{ _id?: unknown, companyID?: unknown } | null | undefined} campaign
 * @param {string | null} employeeID
 * @param {Array<{ question?: { _id?: unknown, type?: string }, isOptional?: boolean }>} questions
 * @param {Record<string, unknown>} answersByQuestionId
 */
export function buildSurveyAnswerPayload(campaign, employeeID, questions, answersByQuestionId) {
  return {
    campaignID: campaign?._id,
    companyID: campaign?.companyID,
    employeeID,
    answers: questions
      .map((questionEntry, index) => {
        const questionKey = getQuestionKey(questionEntry, index);
        const answerID = answersByQuestionId[questionKey];

        if (isOptionalOpenSurveyQuestion(questionEntry) && !hasAnswerValue(questionEntry, answerID)) {
          return null;
        }

        return {
          questionID: questionEntry?.question?._id,
          answerID,
        };
      })
      .filter(Boolean),
  };
}

/**
 * @param {string | Date | null | undefined} dateValue
 * @param {string} locale
 * @returns {string}
 */
export function formatDateLabel(dateValue, locale) {
  if (!dateValue) {
    return '--';
  }

  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(dateValue));
  } catch {
    return new Date(dateValue).toLocaleDateString();
  }
}

/**
 * @returns {typeof SURVEY_VIEW_MODE_STEPPER | typeof SURVEY_VIEW_MODE_LIST}
 */
export function readSurveyViewMode() {
  if (typeof window === 'undefined') {
    return SURVEY_VIEW_MODE_STEPPER;
  }

  try {
    const stored = window.localStorage.getItem(SURVEY_VIEW_MODE_STORAGE_KEY);
    if (stored === SURVEY_VIEW_MODE_LIST || stored === SURVEY_VIEW_MODE_STEPPER) {
      return stored;
    }
  } catch {
    // Ignore private mode / quota failures.
  }

  return SURVEY_VIEW_MODE_STEPPER;
}

/**
 * @param {string} viewMode
 */
export function saveSurveyViewMode(viewMode) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(SURVEY_VIEW_MODE_STORAGE_KEY, viewMode);
  } catch {
    // Ignore private mode / quota failures.
  }
}

/**
 * Normalize a taxonomy field to a non-empty display string.
 * @param {unknown} value
 * @returns {string}
 */
function toTaxonomyLabel(value) {
  if (value == null) {
    return '';
  }
  const text = String(value).trim();
  return text.length > 0 ? text : '';
}

/**
 * Build pill descriptors for driver / Category / Topic when present on a question doc.
 * Omits missing or blank fields. Driver is shown as stored (catalog localization is optional).
 *
 * @param {{ driver?: unknown, Category?: unknown, category?: unknown, Topic?: unknown, topic?: unknown } | null | undefined} question
 * @returns {Array<{ id: 'driver' | 'category' | 'topic', label: string }>}
 */
export function buildSurveyQuestionTaxonomyPills(question) {
  if (!question || typeof question !== 'object') {
    return [];
  }

  /** @type {Array<{ id: 'driver' | 'category' | 'topic', label: string }>} */
  const pills = [];

  const driver = toTaxonomyLabel(question.driver);
  if (driver) {
    pills.push({ id: 'driver', label: driver });
  }

  const category = toTaxonomyLabel(question.Category ?? question.category);
  if (category) {
    pills.push({ id: 'category', label: category });
  }

  const topic = toTaxonomyLabel(question.Topic ?? question.topic);
  if (topic) {
    pills.push({ id: 'topic', label: topic });
  }

  return pills;
}

/**
 * Whether stepper should auto-advance for this question type after a selection.
 * @param {{ question?: { type?: string } } | null | undefined} questionEntry
 * @returns {boolean}
 */
export function shouldAutoAdvanceSurveyStepper(questionEntry) {
  const questionType = questionEntry?.question?.type;
  return questionType !== 'open';
}

/**
 * Whether the active UI language should prefer Spanish pulse catalogue fields (`*_ESP`).
 * @param {string | null | undefined} language i18n language (e.g. `es`, `es-ES`, `en`)
 * @returns {boolean}
 */
export function prefersSpanishPulseUiLanguage(language) {
  return String(language || '')
    .toLowerCase()
    .startsWith('es');
}

/**
 * Pick English (`Resp*_String` / `Question`) vs Spanish (`*_ESP`) catalogue copy for the UI language.
 * Falls back to the other locale when the preferred label is blank.
 *
 * @param {string | null | undefined} language
 * @param {unknown} englishValue
 * @param {unknown} spanishValue
 * @returns {string}
 */
export function pickLocalizedPulseFieldLabel(language, englishValue, spanishValue) {
  const english = String(englishValue ?? '').trim();
  const spanish = String(spanishValue ?? '').trim();
  if (prefersSpanishPulseUiLanguage(language)) {
    return spanish || english;
  }
  return english || spanish;
}

/**
 * Ordered Resp1…Resp5 labels for the active UI language (empty strings kept for slot alignment).
 *
 * @param {{
 *   Resp1_String?: unknown, Resp1_String_ESP?: unknown,
 *   Resp2_String?: unknown, Resp2_String_ESP?: unknown,
 *   Resp3_String?: unknown, Resp3_String_ESP?: unknown,
 *   Resp4_String?: unknown, Resp4_String_ESP?: unknown,
 *   Resp5_String?: unknown, Resp5_String_ESP?: unknown,
 * } | null | undefined} question
 * @param {string | null | undefined} language
 * @returns {string[]}
 */
export function getLocalizedPulseScaleResponseLabels(question, language) {
  return [
    pickLocalizedPulseFieldLabel(language, question?.Resp1_String, question?.Resp1_String_ESP),
    pickLocalizedPulseFieldLabel(language, question?.Resp2_String, question?.Resp2_String_ESP),
    pickLocalizedPulseFieldLabel(language, question?.Resp3_String, question?.Resp3_String_ESP),
    pickLocalizedPulseFieldLabel(language, question?.Resp4_String, question?.Resp4_String_ESP),
    pickLocalizedPulseFieldLabel(language, question?.Resp5_String, question?.Resp5_String_ESP),
  ];
}

/**
 * Question stem for the active UI language (`Question` EN vs `Question_ESP`).
 * Same fallback rules as scale / answer labels.
 *
 * @param {{ Question?: unknown, Question_ESP?: unknown } | null | undefined} question
 * @param {string | null | undefined} language
 * @returns {string}
 */
export function getLocalizedPulseQuestionText(question, language) {
  return pickLocalizedPulseFieldLabel(language, question?.Question, question?.Question_ESP);
}

/**
 * Resolve a stored answer value to the human-readable label for review UI.
 * Scale answers are stored as answerIDs `"k"…"1"` from filled Resps; RNA as `"rna"`;
 * open/multiselect keep their strings.
 *
 * @param {{ question?: {
 *   type?: string,
 *   Resp1_String?: unknown, Resp1_String_ESP?: unknown,
 *   Resp2_String?: unknown, Resp2_String_ESP?: unknown,
 *   Resp3_String?: unknown, Resp3_String_ESP?: unknown,
 *   Resp4_String?: unknown, Resp4_String_ESP?: unknown,
 *   Resp5_String?: unknown, Resp5_String_ESP?: unknown,
 * } } | null | undefined} questionEntry
 * @param {unknown} answerValue
 * @param {{ rnaLabel?: string, language?: string | null }} [options]
 * @returns {string}
 */
export function formatSurveyAnswerDisplayLabel(questionEntry, answerValue, options = {}) {
  if (Array.isArray(answerValue)) {
    return answerValue
      .filter((value) => typeof value === 'string' && value.trim() !== '')
      .join(', ');
  }

  if (typeof answerValue !== 'string' || answerValue.trim() === '') {
    return '';
  }

  const questionType = questionEntry?.question?.type;
  if (questionType === 'open' || questionType === 'multiselect') {
    return answerValue;
  }

  if (answerValue === 'rna') {
    return options.rnaLabel ?? 'rna';
  }

  const scaleOptions = buildStandardPulseScaleOptions(
    getLocalizedPulseScaleResponseLabels(questionEntry?.question, options.language)
  );
  const match = scaleOptions.find((option) => option.value === answerValue);
  if (!match) {
    return answerValue;
  }

  const text = String(match.label ?? '').trim();
  return text.length > 0 ? text : answerValue;
}

/**
 * Resolve the rather-not-answer choice label from the question document or i18n fallback.
 *
 * @param {{ Rather_Not_Answer_String_ESP?: unknown, ratherNotAnswerStringEsp?: unknown } | null | undefined} question
 * @param {string} fallbackI18n
 * @returns {string}
 */
export function getSurveyRnaLabel(question, fallbackI18n) {
  const custom = String(
    question?.Rather_Not_Answer_String_ESP ?? question?.ratherNotAnswerStringEsp ?? ''
  ).trim();
  return custom.length > 0 ? custom : fallbackI18n;
}

/**
 * Split plain text into text/link segments for safe React rendering. Only http(s) URLs.
 *
 * @param {string | null | undefined} text
 * @returns {Array<{ type: 'text' | 'link', value: string }>}
 */
export function linkifySurveyDescriptionPlainText(text) {
  const source = String(text ?? '');
  if (source.length === 0) {
    return [];
  }

  const urlPattern = /https?:\/\/[^\s<>"']+/gi;
  /** @type {Array<{ type: 'text' | 'link', value: string }>} */
  const segments = [];
  let lastIndex = 0;
  let match = urlPattern.exec(source);

  while (match) {
    const url = match[0];
    const start = match.index;
    if (start > lastIndex) {
      segments.push({ type: 'text', value: source.slice(lastIndex, start) });
    }
    // Trim common trailing punctuation from the link while keeping it as text after.
    let linkValue = url;
    let trailing = '';
    while (/[.,);:!?]$/.test(linkValue) && linkValue.length > 0) {
      trailing = linkValue.slice(-1) + trailing;
      linkValue = linkValue.slice(0, -1);
    }
    if (linkValue.length > 0 && isSafeRemoteImageUrl(linkValue)) {
      segments.push({ type: 'link', value: linkValue });
      if (trailing.length > 0) {
        segments.push({ type: 'text', value: trailing });
      }
    } else {
      segments.push({ type: 'text', value: url });
    }
    lastIndex = start + url.length;
    match = urlPattern.exec(source);
  }

  if (lastIndex < source.length) {
    segments.push({ type: 'text', value: source.slice(lastIndex) });
  }

  return segments;
}
